"use client";

import React from "react";
import Link from "next/link";
import {
  Layout,
  HelpCircle,
  Shield,
  Moon,
  Sun,
  MessageCircle,
} from "lucide-react";
import { DropdownMenuItem, DropdownMenuLabel } from "../../dropdown-menu";
import { Switch } from "../../switch";

interface PreferencesSectionProps {
  theme: "light" | "dark";
  toggleTheme: () => void;
  isEmployee: boolean;
  isAdmin: boolean;
  hasNavigationItems: boolean;
  onOpenNavCustomization: () => void;
  isSupportChatEnabled: boolean;
  onOpenSupportChat: () => void;
}

export function PreferencesSection({
  theme,
  toggleTheme,
  isEmployee,
  isAdmin,
  hasNavigationItems,
  onOpenNavCustomization,
  isSupportChatEnabled,
  onOpenSupportChat,
}: PreferencesSectionProps) {
  return (
    <>
      <DropdownMenuLabel className="px-3 py-2 text-xs font-medium text-muted-foreground uppercase tracking-wider">
        Preferences
      </DropdownMenuLabel>

      {/* Dark Mode Toggle */}
      <DropdownMenuItem
        className="flex items-center justify-between px-3 py-2 cursor-pointer"
        onSelect={(e) => e.preventDefault()}
      >
        <div className="flex items-center">
          {theme === "dark" ? (
            <Moon className="mr-3 h-4 w-4 text-muted-foreground" />
          ) : (
            <Sun className="mr-3 h-4 w-4 text-muted-foreground" />
          )}
          <div>
            <div className="font-medium text-foreground">Dark mode (Beta)</div>
            <div className="text-xs text-muted-foreground">Toggle theme</div>
          </div>
        </div>
        <Switch checked={theme === "dark"} onCheckedChange={() => toggleTheme()} />
      </DropdownMenuItem>

      {/* Navigation Customization - Employee/Admin only */}
      {(isEmployee || isAdmin) && hasNavigationItems && (
        <DropdownMenuItem
          className="flex items-center px-3 py-2 cursor-pointer"
          onClick={onOpenNavCustomization}
        >
          <Layout className="mr-3 h-4 w-4 text-muted-foreground" />
          <div>
            <div className="font-medium text-foreground">Customize Navigation</div>
            <div className="text-xs text-muted-foreground">Organize your menu</div>
          </div>
        </DropdownMenuItem>
      )}

      {/* Help & Support */}
      <DropdownMenuItem asChild>
        <Link href="/docs" className="flex items-center px-3 py-2 cursor-pointer">
          <HelpCircle className="mr-3 h-4 w-4 text-muted-foreground" />
          <div>
            <div className="font-medium text-foreground">Help & Support</div>
            <div className="text-xs text-muted-foreground">Documentation & guides</div>
          </div>
        </Link>
      </DropdownMenuItem>

      {/* Live Chat - Feature gated */}
      {isSupportChatEnabled && (
        <DropdownMenuItem
          className="flex items-center px-3 py-2 cursor-pointer"
          onClick={onOpenSupportChat}
        >
          <MessageCircle className="mr-3 h-4 w-4 text-muted-foreground" />
          <div>
            <div className="font-medium text-foreground">Live chat</div>
            <div className="text-xs text-muted-foreground">Chat with support</div>
          </div>
        </DropdownMenuItem>
      )}

      {/* Admin Panel - Admin only */}
      {isAdmin && (
        <DropdownMenuItem asChild>
          <Link href="/admin" className="flex items-center px-3 py-2 cursor-pointer">
            <Shield className="mr-3 h-4 w-4 text-muted-foreground" />
            <div>
              <div className="font-medium text-foreground">Admin Panel</div>
              <div className="text-xs text-muted-foreground">System administration</div>
            </div>
          </Link>
        </DropdownMenuItem>
      )}
    </>
  );
}
